import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Statistics {
  total_sync_jobs: number
  active_jobs: number
  records_synced_today: number
  records_synced_mtd: number
  sync_success_rate: number
  avg_sync_duration_sec: number
}

interface SyncJob {
  id: string
  name: string
  source: string
  destination: string
  frequency: string
  last_run: string
  status: string
}

interface SyncHistory {
  id: string
  job_name: string
  started: string
  duration: string
  records: number
  status: string
}

export default function DataSyncPage() {
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [jobs, setJobs] = useState<SyncJob[]>([])
  const [history, setHistory] = useState<SyncHistory[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, jobsRes, historyRes] = await Promise.all([
          fetch('/api/datasync/statistics'),
          fetch('/api/datasync/jobs'),
          fetch('/api/datasync/history')
        ])
        setStatistics(await statsRes.json())
        setJobs(await jobsRes.json())
        setHistory(await historyRes.json())
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      'Running': 'bg-blue-100 text-blue-800',
      'Scheduled': 'bg-yellow-100 text-yellow-800',
      'Completed': 'bg-green-100 text-green-800',
      'Failed': 'bg-red-100 text-red-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return <div className="p-8"><Header /><div className="mt-8">Loading Data Sync...</div></div>
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Data Synchronization</h1>

        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-blue-600">{statistics.total_sync_jobs}</div>
              <div className="text-sm text-gray-500">Total Jobs</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-green-600">{statistics.active_jobs}</div>
              <div className="text-sm text-gray-500">Active Jobs</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-purple-600">{(statistics.records_synced_today / 1000000).toFixed(1)}M</div>
              <div className="text-sm text-gray-500">Records Today</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-indigo-600">{(statistics.records_synced_mtd / 1000000).toFixed(0)}M</div>
              <div className="text-sm text-gray-500">Records MTD</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-orange-600">{statistics.sync_success_rate}%</div>
              <div className="text-sm text-gray-500">Success Rate</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-teal-600">{statistics.avg_sync_duration_sec}s</div>
              <div className="text-sm text-gray-500">Avg Duration</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Sync Jobs</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Job Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Source</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Destination</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {jobs.map((job) => (
                    <tr key={job.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{job.name}</td>
                      <td className="px-4 py-3 text-gray-600">{job.source}</td>
                      <td className="px-4 py-3 text-gray-600">{job.destination}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(job.status)}`}>
                          {job.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Sync History</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Job</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Duration</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Records</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {history.map((h) => (
                    <tr key={h.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{h.job_name}</td>
                      <td className="px-4 py-3 text-gray-600">{h.duration}</td>
                      <td className="px-4 py-3 text-gray-600">{h.records.toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(h.status)}`}>
                          {h.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
